import { Brain } from 'lucide-react';
import { z } from 'zod';

import { defineStandalone } from '@apps/web/features/phoebe_chat_v3/registry/types';
import { EventLine } from '@apps/web/features/phoebe_chat_v3/ui/primitives/event_line';

// The summary_text and replaced item ids are model internals; the transcript
// only records that Phoebe compacted its context, in plain words.
const PRESENT_COPY = 'Phoebe is reorganizing its thoughts…';
const PAST_COPY = 'Phoebe reorganized its thoughts.';

// `handoff_note` is accepted but never rendered: the card is only a marker
// that history was compacted. The note stays in the payload for the debug
// panel and traces.
const payloadSchema = z.strictObject({
    summary_item_id: z.uuid(),
    summary_text: z.string().nullable().optional(),
    replaced_item_ids: z.array(z.uuid()),
    replaced_item_count: z.number().int(),
    preserved_item_count: z.number().int(),
    reason: z.enum(['pre_flight', 'post_turn', 'requested']),
    handoff_note: z.string().optional(),
});

export const itemCompactionDefinitions = {
    item_compaction: defineStandalone('item_compaction', {
        disposition: 'render',
        schema: payloadSchema,
        malformed: {
            kind: 'placeholder',
            customerCopy: PAST_COPY,
        },
        evidence: null,
        group: null,
        render(_payload, occurrence) {
            return (
                <EventLine glyph={<Brain />}>
                    {occurrence.phase === 'committed'
                        ? PAST_COPY
                        : PRESENT_COPY}
                </EventLine>
            );
        },
    }),
} as const;

export const itemCompactionContractFixtures = {
    valid: {
        summary_item_id: '01991a70-e12d-754a-bbfe-f94ee7fb6348',
        summary_text: 'summary-text-sentinel',
        replaced_item_ids: ['01991a71-6519-73b0-bba1-e7bbcb7c7f5e'],
        replaced_item_count: 1,
        preserved_item_count: 2,
        reason: 'requested',
        handoff_note: 'handoff-note-sentinel',
    },
    malformed: {
        summary_item_id: '01991a70-e12d-754a-bbfe-f94ee7fb6348',
        summary_text: 'summary-text-sentinel',
        replaced_item_ids: ['01991a71-6519-73b0-bba1-e7bbcb7c7f5e'],
        replaced_item_count: 1,
        preserved_item_count: 2,
        reason: 'post_turn',
        unexpected_detail: 'unexpected-detail-sentinel',
    },
} as const;
